import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  OnChanges,
  ChangeDetectorRef,
  EventEmitter,
  Output,
  inject,
  signal,
} from '@angular/core';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { Subscription, Observable } from 'rxjs';
import { select, Store } from '@ngxs/store';
import { AdditionalServicesOffering } from '@shared/models/additional-services-offering.model';
import {
  AdditionalServiceVoice,
  AdditionalServiceSet,
  AdditionalServiceMutex,
} from '../../../models/additional-service-voice.model';
import {
  AdditionalServiceAddAction,
  AdditionalServiceRemoveAction,
  AdditionalServiceUpdateICCAction,
  AdditionalServiceUpdateOrderSimAction,
  SetSelectedStepIndexAction,
  UpdateWhitelistEntriesAction,
} from '@sales/states/customer-product/customer-product.actions';
import {
  FormGroup,
  FormControl,
  Validators,
  FormArray,
  AsyncValidatorFn,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ControlContainer, FormGroupDirective } from '@angular/forms';
import { ErrorStateOnTouched } from '@app/material/ErrorStateOnTouched';
import { PriceType } from '@shared/pipes/price-format.pipe';
import { Regex } from '@shared/enums/regex';
import { SimService } from '@sales/services/sim.service';
import { SimCardDelivery, SimCardType } from '@sales/models/simcard.model';
import { SimCardValidator } from '@sales/validators/async-sim-card-validator';
import { IconNames } from '@shared/enums/icon-names';
import { OfferingSelectors } from '@sales/states/offering/offering.selectors';
import { FeatureGateService } from '@shared/services/featuregate.service';
import { CustomerProductSelectors } from '@sales/states/customer-product/customer-product.selectors';
import { OfferingHelperService } from '@app/sales/services/offering-helper.service';
import { getUniqueSellingPointsTitle } from '@app/sales/modules/subscription/constants/value-added-service-product';
import { VoiceSubscriptionType } from '../../../models/offerings-response.model';
import { SubscriptionHelperService } from '@app/sales/services/subscription-helper.service';
import { PriceFormatPipe } from '@app/shared/pipes/price-format.pipe';
import { IcePillComponent } from '@app/shared/components/atoms/ice-pill/ice-pill.component';
import { MutexAdditionalServiceComponent } from './mutex-additional-service/mutex-additional-service.component';
import { AdditionalServiceComponent } from './additional-service/additional-service.component';
import { IncludedServicesService } from '@app/sales/modules/subscription/services/included-services.service';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { WhitelistEntriesInputComponent } from './whitelist-entries-input/whitelist-entries-input.component';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { AdditionalSimGroupComponent, SimRow } from '../../additional-sim-group/additional-sim-group.component';

@Component({
  selector: 'app-additional-services-step',
  templateUrl: './additional-services-step.component.html',
  styleUrls: ['./additional-services-step.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    SpinnerComponent,
    WhitelistEntriesInputComponent,
    FormsModule,
    ReactiveFormsModule,
    IceAlertComponent,
    AdditionalServiceComponent,
    MutexAdditionalServiceComponent,
    IcePillComponent,
    PriceFormatPipe,
    AdditionalSimGroupComponent,
  ],
})
export class AdditionalServicesStepComponent implements OnInit, OnChanges, OnDestroy {
  private store = inject(Store);
  private ref = inject(ChangeDetectorRef);
  private formGroupDirective = inject(FormGroupDirective);
  private simService = inject(SimService);
  private featureGateService = inject(FeatureGateService);
  private includedServices = inject(IncludedServicesService);

  PriceType = PriceType;
  IconNames = IconNames;
  getVasProductFromOffering = OfferingHelperService.getVasProductFromOffering;
  getUniqueSellingPointsTitle = getUniqueSellingPointsTitle;

  @Input() product: VoiceProduct;
  @Input() showServices: boolean = true;
  @Output() isInvalid = new EventEmitter();

  additionalServicesOffering$: Observable<AdditionalServicesOffering[]> = this.store.select<AdditionalServicesOffering[]>(
    state => (state.offerings ? state.offerings.services : [])
  );
  isLoadingAdditionalServiceOfferings = select(OfferingSelectors.isLoadingAdditionalServicesForOffering);

  additionalServicesForProduct: AdditionalServicesOffering;
  additionalServicesFromCache: AdditionalServicesOffering[] = [];

  form: FormArray;
  matcher = new ErrorStateOnTouched();
  whitelistEntryForm: FormGroup;

  subscriptions$: Subscription[] = [];

  simRows = signal<Record<string, SimRow[]>>({});
  multipleExtraSims = this.featureGateService.isFeatureEnabled('featureMultipleExtraSims');

  get simServices(): AdditionalServiceVoice[] {
    if (!this.multipleExtraSims) {
      return [];
    }
    return (
      this.additionalServicesForProduct?.additionalServices.serviceProducts.filter(
        AdditionalServicesStepComponent.isAdditionalSim
      ) ?? []
    );
  }

  get mutexAndNonSimServiceControls(): FormGroup[] {
    return (this.form.controls as FormGroup[]).filter(
      item =>
        !this.multipleExtraSims ||
        item.get('isMutexService') ||
        !AdditionalServicesStepComponent.isAdditionalSim(this.getAdditionalService(item))
    );
  }

  get isIceJunior(): boolean {
    const offeringNameMatch = SubscriptionHelperService.isProductIceJunior(this.product.offering?.subscriptionType);
    return this.product.offering.subscriptionType == VoiceSubscriptionType.IceJunior && offeringNameMatch;
  }

  private static buildAdditionalServicesFormArray(
    additionalServicesForProduct: AdditionalServiceSet,
    product: VoiceProduct,
    iccValidator: AsyncValidatorFn,
    groupSims: boolean
  ): FormGroup[] {
    const servicesList = additionalServicesForProduct.serviceProducts
      .filter(service => !groupSims || !AdditionalServicesStepComponent.isAdditionalSim(service))
      .map(additionalService => {
        const selectedAdditionalService = product.additionalServices.find(s => s.productId === additionalService.productId);
        const additionalServiceForm = AdditionalServicesStepComponent.buildAdditionalServiceFormGroup(
          additionalService,
          selectedAdditionalService,
          iccValidator
        );
        return additionalServiceForm;
      });

    return servicesList.concat(
      additionalServicesForProduct.mutexServiceSets.map(mutexSet => {
        const selectedMutexService = mutexSet.mutexServices.find(mutexService => {
          return product.additionalServices.map(s => s.productId).includes(mutexService.productId);
        });
        const mutexServiceForm = AdditionalServicesStepComponent.buildMutexServiceFormGroup(
          mutexSet.name,
          !!selectedMutexService,
          selectedMutexService?.productId
        );
        return mutexServiceForm;
      })
    );
  }

  public static buildAdditionalServiceFormGroup(
    additionalService: AdditionalServiceVoice,
    selectedAdditionalService: AdditionalServiceVoice,
    iccValidator: AsyncValidatorFn
  ): FormGroup {
    if (additionalService.requireICC) {
      return AdditionalServicesStepComponent.buildICCFormGroup(
        additionalService.productId,
        !!selectedAdditionalService,
        selectedAdditionalService?.icc,
        SubscriptionHelperService.getSimCardDelivery(selectedAdditionalService?.icc, selectedAdditionalService?.orderSim),
        iccValidator
      );
    } else {
      return AdditionalServicesStepComponent.buildFormGroup(
        additionalService.productId,
        !!selectedAdditionalService,
        additionalService.isCampaign
      );
    }
  }

  public static buildMutexServiceFormGroup(name: string, selected: boolean, selectedMutexServiceId: string): FormGroup {
    const form = new FormGroup({
      isMutexService: new FormControl(true),
      mutexName: new FormControl(name),
      selected: new FormControl(selected),
      selectedMutexService: new FormControl({
        value: selectedMutexServiceId,
        disabled: !selectedMutexServiceId,
      }),
    });
    return form;
  }

  public static buildFormGroup(productId: string, selected: boolean, isDisabled: boolean): FormGroup {
    return new FormGroup({
      additionalServiceId: new FormControl(productId),
      selected: new FormControl({ value: selected, disabled: isDisabled }),
    });
  }

  public static buildICCFormGroup(
    productId: string,
    selected: boolean,
    icc: string,
    simDelivery: SimCardDelivery,
    iccValidator: AsyncValidatorFn
  ): FormGroup {
    const form = new FormGroup({
      additionalServiceId: new FormControl(productId),
      requireICC: new FormControl(true),
      selected: new FormControl(!!selected),
      simDelivery: new FormControl(
        {
          value: simDelivery ?? null,
          disabled: !selected,
        },
        Validators.required
      ),
      icc: new FormControl(
        {
          value: icc,
          disabled: !selected || simDelivery !== SimCardDelivery.InStore,
        },
        [Validators.required, Validators.pattern(Regex.ICC), Validators.minLength(19), Validators.maxLength(20)],
        [iccValidator]
      ),
    });

    return form;
  }

  private static isAdditionalSim(service: AdditionalServiceVoice): boolean {
    return !!(service?.isDataSimCard || service?.isTwinSimCard);
  }

  private static syncIccAvailability(group: FormGroup) {
    const needsIcc = group.controls.selected.value && group.controls.simDelivery.value === SimCardDelivery.InStore;

    if (needsIcc) {
      group.controls.icc.enable();
    } else {
      group.controls.icc.disable();
    }
  }

  ngOnInit() {
    this.buildWhitelistEntriesForm();
    this.subscriptions$.push(
      this.whitelistEntryForm.valueChanges.pipe(debounceTime(500)).subscribe(formValue => {
        this.isInvalid.emit(this.whitelistEntryForm?.invalid || this.form?.invalid);
        this.store.dispatch(new UpdateWhitelistEntriesAction(this.product.id, formValue));
      })
    );
    this.subscriptions$.push(
      this.additionalServicesOffering$.subscribe({
        next: (additionalServicesOfferings: AdditionalServicesOffering[]) => {
          if (!additionalServicesOfferings) {
            return;
          }

          this.additionalServicesFromCache = additionalServicesOfferings;

          const id = this.product?.isExistingSubscription ? this.product?.id : this.product?.offering?.id;
          if (!id) {
            this.clearForm();
          } else if (id !== this.additionalServicesForProduct?.parentOfferingId) {
            this.additionalServicesForProduct = additionalServicesOfferings.find(s => s.parentOfferingId === id);
            if (this.additionalServicesForProduct) {
              this.buildForm();
              this.ref.detectChanges();
            }
          }
        },
      })
    );
  }

  ngOnChanges(changes) {
    if (changes.product) {
      const newValue: VoiceProduct = changes.product.currentValue;
      const oldValue: VoiceProduct = changes.product.previousValue;

      if (
        oldValue?.offering?.id !== newValue?.offering?.id &&
        newValue?.offering?.id !== this.additionalServicesForProduct?.parentOfferingId
      ) {
        this.additionalServicesForProduct = this.additionalServicesFromCache.find(
          s => s.parentOfferingId === newValue?.offering?.id
        );

        if (this.additionalServicesForProduct) {
          this.buildForm();
        }
      }
    }
  }

  private buildWhitelistEntriesForm() {
    if (!this.formGroupDirective.form.get('whitelistEntries')) {
      this.formGroupDirective.form.addControl(
        'whitelistEntries',
        new FormGroup({
          whitelistEntry1: new FormControl('', [Validators.pattern(Regex.Phone)]),
          whitelistEntry2: new FormControl('', [Validators.pattern(Regex.Phone)]),
          whitelistEntry3: new FormControl('', [Validators.pattern(Regex.Phone)]),
        })
      );
    }
    this.whitelistEntryForm = this.formGroupDirective.form.get('whitelistEntries') as FormGroup;
  }

  private buildForm() {
    this.clearForm();
    const formArray = AdditionalServicesStepComponent.buildAdditionalServicesFormArray(
      this.additionalServicesForProduct.additionalServices,
      this.product,
      SimCardValidator(this.simService, SimCardType.Voice, () => {
        this.form.updateValueAndValidity();
        this.isInvalid.emit(this.form?.invalid || this.whitelistEntryForm?.invalid);
      }),
      this.multipleExtraSims
    );
    this.setValueChangeEventOnSelection(formArray);
    this.setValueChangeEventOnIccControls(formArray);
    this.setValueChangeEventOnMutexServices(formArray);

    formArray.forEach(group => this.form.push(group));
    this.simRows.set(
      Object.fromEntries(
        this.simServices.map(service => [
          service.productId,
          this.storedServices(service.productId).map(stored => this.attachSimRow(service, stored)),
        ])
      )
    );
    this.selectIncludedServices();

    this.subscriptions$.push(
      this.form.statusChanges.subscribe(() => {
        this.isInvalid.emit(this.form?.invalid || this.whitelistEntryForm?.invalid);
      })
    );

    this.isInvalid.emit(this.form?.invalid || this.whitelistEntryForm?.invalid);
  }

  // Set after the form is built, because the resulting valueChanges is what adds the service to the product.
  private selectIncludedServices() {
    if (this.product?.isExistingSubscription) {
      return;
    }

    (this.form.controls as FormGroup[])
      .filter(
        group =>
          !group.controls.isMutexService &&
          !group.controls.selected.value &&
          this.includedServices.isIncluded(this.getAdditionalService(group))
      )
      .forEach(group => group.controls.selected.setValue(true));
  }

  private clearForm() {
    this.simRows.set({});
    if (this.formGroupDirective.form.get('additionalServices')) {
      this.form = this.formGroupDirective.form.get('additionalServices') as FormArray;
      this.form.clear();
    } else {
      this.formGroupDirective.form.addControl('additionalServices', new FormArray([]));
      this.form = this.formGroupDirective.form.get('additionalServices') as FormArray;
    }
  }
  private setValueChangeEventOnSelection(formArray: FormGroup[]) {
    formArray.forEach(serviceFormGroup => {
      this.subscriptions$.push(
        serviceFormGroup.controls.selected.valueChanges.subscribe((checked: boolean) => {
          if (serviceFormGroup.controls.isMutexService) {
            this.updateMutexService(checked, this.getMutexService(serviceFormGroup), serviceFormGroup);
          } else {
            const additionalService = this.getAdditionalService(serviceFormGroup);
            if (!additionalService.isCampaign || additionalService.requireICC) {
              this.updateAdditionalServices(checked, additionalService, serviceFormGroup);
            }
          }
        })
      );
    });
  }

  private setValueChangeEventOnSimRow(group: FormGroup, instance: AdditionalServiceVoice) {
    this.subscriptions$.push(
      group.controls.icc.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(icc => {
        this.store.dispatch(new AdditionalServiceUpdateICCAction(this.product, instance, icc));
      })
    );
    this.subscriptions$.push(
      group.controls.simDelivery.valueChanges.subscribe((simDelivery: SimCardDelivery) => {
        if (simDelivery !== SimCardDelivery.InStore) {
          group.controls.icc.patchValue('');
        }
        AdditionalServicesStepComponent.syncIccAvailability(group);
        this.store.dispatch(
          new AdditionalServiceUpdateOrderSimAction(this.product, instance, simDelivery === SimCardDelivery.ByMail)
        );
      })
    );
  }

  private setValueChangeEventOnIccControls(formArray: FormGroup[]) {
    formArray.forEach(group => {
      if (group.controls.icc) {
        const additionalService = this.getAdditionalService(group);
        this.subscriptions$.push(
          group.controls.icc.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(icc => {
            this.store.dispatch(new AdditionalServiceUpdateICCAction(this.product, additionalService, icc));
          })
        );
        this.subscriptions$.push(
          group.get('simDelivery').valueChanges.subscribe((simDelivery: SimCardDelivery) => {
            if (!group.controls.selected.value) {
              return;
            }
            if (simDelivery !== SimCardDelivery.InStore) {
              group.get('icc').patchValue('');
            }
            AdditionalServicesStepComponent.syncIccAvailability(group);
            this.store.dispatch(
              new AdditionalServiceUpdateOrderSimAction(
                this.product,
                additionalService,
                simDelivery === SimCardDelivery.ByMail
              )
            );
          })
        );
      }
    });
  }

  private setValueChangeEventOnMutexServices(formArray: FormGroup[]) {
    formArray.forEach(group => {
      if (group.controls.isMutexService) {
        this.subscriptions$.push(
          group.controls.selectedMutexService.valueChanges.subscribe(newValue => {
            if (!newValue) {
              const toDelete = this.product.additionalServices.find(x => x.mutexSetName === group.controls.mutexName.value);
              if (toDelete) {
                this.store.dispatch(new AdditionalServiceRemoveAction(this.product.id, toDelete));
              }
            } else {
              const newService = this.additionalServicesForProduct?.additionalServices.mutexServiceSets
                .find(set => set.name === group.controls.mutexName.value)
                ?.mutexServices.find(service => service.productId === newValue);
              if (newService) {
                this.store.dispatch(
                  new AdditionalServiceAddAction(
                    this.product.id,
                    { ...newService, mutexSetName: group.controls.mutexName.value },
                    true
                  )
                );
              }
            }
          })
        );
      }
    });
  }

  getAdditionalService(item: FormGroup): AdditionalServiceVoice {
    const additionalService = this.additionalServicesForProduct?.additionalServices.serviceProducts.find(
      s => s.productId === item.get('additionalServiceId').value
    );
    return additionalService;
  }

  getMutexService(item: FormGroup): AdditionalServiceMutex {
    const mutexServiceSet = this.additionalServicesForProduct?.additionalServices.mutexServiceSets.find(
      s => s.name === item.controls.mutexName.value
    );
    return mutexServiceSet;
  }

  updateAdditionalServices(checked: boolean, additionalService: AdditionalServiceVoice, item: FormGroup) {
    if (checked) {
      if (additionalService.requireICC) {
        item.get('simDelivery').enable();
        AdditionalServicesStepComponent.syncIccAvailability(item);
      }
      this.store.dispatch(new AdditionalServiceAddAction(this.product.id, additionalService));
      if (item.controls.simDelivery) {
        this.store.dispatch(
          new AdditionalServiceUpdateOrderSimAction(
            this.product,
            additionalService,
            item.controls.simDelivery.value === SimCardDelivery.ByMail
          )
        );
      }
    } else {
      if (additionalService.requireICC) {
        item.get('simDelivery').disable();
        item.get('icc').disable();
      }
      this.store.dispatch(new AdditionalServiceRemoveAction(this.product.id, additionalService));
    }
  }

  updateMutexService(checked: boolean, mutexSet: AdditionalServiceMutex, item: FormGroup) {
    if (checked) {
      item.controls.selectedMutexService.enable();
      if (!!mutexSet?.mutexServices.length) {
        item.controls.selectedMutexService.setValue(mutexSet.mutexServices[0].productId);
      }
    } else {
      item.controls.selectedMutexService.disable();
      item.controls.selectedMutexService.setValue('');
    }
  }

  private storedServices(productId: string): AdditionalServiceVoice[] {
    const product = this.store
      .selectSnapshot(CustomerProductSelectors.getAllVoiceProducts)
      .find(p => p.id === this.product.id);
    return product?.additionalServices.filter(a => a.productId === productId) ?? [];
  }

  private attachSimRow(service: AdditionalServiceVoice, instance: AdditionalServiceVoice): SimRow {
    const form = AdditionalServicesStepComponent.buildICCFormGroup(
      service.productId,
      true,
      instance.icc,
      SubscriptionHelperService.getSimCardDelivery(instance.icc, instance.orderSim),
      SimCardValidator(this.simService, SimCardType.Voice, () => {
        this.form.updateValueAndValidity();
        this.isInvalid.emit(this.form?.invalid || this.whitelistEntryForm?.invalid);
      })
    );
    this.setValueChangeEventOnSimRow(form, instance);
    this.form.push(form);
    return { instanceId: instance.instanceId, form };
  }

  addSimRow(service: AdditionalServiceVoice) {
    const before = this.storedServices(service.productId).map(a => a.instanceId);
    this.store.dispatch(new AdditionalServiceAddAction(this.product.id, service));

    const added = this.storedServices(service.productId).find(a => !before.includes(a.instanceId));
    if (!added) {
      return;
    }

    const row = this.attachSimRow(service, added);
    this.simRows.update(rows => ({ ...rows, [service.productId]: [...rows[service.productId], row] }));
  }

  removeSimRow(service: AdditionalServiceVoice, row: SimRow) {
    const stored = this.storedServices(service.productId).find(a => a.instanceId === row.instanceId);
    if (stored) {
      this.store.dispatch(new AdditionalServiceRemoveAction(this.product.id, stored));
    }

    const index = this.form.controls.indexOf(row.form);
    if (index > -1) {
      this.form.removeAt(index);
    }
    this.simRows.update(rows => ({ ...rows, [service.productId]: rows[service.productId].filter(r => r !== row) }));
  }

  clearSimRows(service: AdditionalServiceVoice) {
    this.simRows()[service.productId].forEach(row => this.removeSimRow(service, row));
  }

  onVasClick() {
    this.store.dispatch(new SetSelectedStepIndexAction(0));
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
